import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, Gauge, Users, Database, Calendar, FileText, ExternalLink, Loader2, XCircle, RotateCcw } from "lucide-react"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface Usage {
  tier: string
  status: string
  events_used: number
  events_limit: number
  seats_used: number
  seats_limit: number
  retention_days: number
  period_start: string
  period_end: string
  trial_ends_at: string | null
}

interface Invoice {
  id: string
  amount: number
  currency: string
  status: string
  product_id: string
  plan?: string
  invoice_url: string
  created_at: string
}

const UNLIMITED = -1

const TIER_LABEL: Record<string, string> = {
  trial: "Trial",
  pulse: "Pulse",
  stream: "Stream",
  surge: "Surge",
  torrent: "Torrent",
  free: "Free",
}

export function UsagePage() {
  const [usage, setUsage] = useState<Usage | null>(null)
  const [loading, setLoading] = useState(true)
  const [renderedAt] = useState(() => Date.now())

  useEffect(() => {
    let cancelled = false
    fetch("/api/v1/saas/usage", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: Usage) => {
        if (!cancelled) setUsage(data)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading || !usage) {
    return (
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold tracking-tight">Usage & plan</h1>
        <p className="mt-2 text-sm text-muted-foreground">Loading your usage…</p>
      </div>
    )
  }

  const renews = new Date(usage.period_end).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
  })
  const trialDaysLeft = usage.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(usage.trial_ends_at).getTime() - renderedAt) / 86_400_000))
    : null

  const hasActiveSub = usage.status === "active" || usage.status === "past_due"
  const isCancelled = usage.status === "cancelled"
  const labelForCta =
    usage.tier === "stream" || usage.tier === "surge" || usage.tier === "torrent"
      ? "Change plan"
      : "Upgrade"

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Usage & plan</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            You're on the
            <Badge variant="secondary" className="font-semibold">
              {TIER_LABEL[usage.tier] ?? usage.tier}
            </Badge>
            plan.
            {usage.status === "trialing" && trialDaysLeft !== null && (
              <span>
                {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left in your trial.
              </span>
            )}
            {isCancelled && (
              <span className="text-amber-500">Cancels on {renews}.</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveSub && <CancelPlanButton onCancelled={() => location.reload()} />}
          {isCancelled && <ReactivatePlanButton onReactivated={() => location.reload()} />}
          <Button asChild className="bg-rose-600 text-white hover:bg-rose-500">
            <Link to="/upgrade">
              {labelForCta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Gauge className="h-4 w-4 text-rose-500" />
              Events this month
            </div>
            <div className="text-sm tabular-nums text-muted-foreground">
              {usage.events_used.toLocaleString()}
              {usage.events_limit === UNLIMITED
                ? " events"
                : ` / ${usage.events_limit.toLocaleString()}`}
            </div>
          </div>
          {usage.events_limit !== UNLIMITED && (
            <Meter used={usage.events_used} limit={usage.events_limit} />
          )}
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
            <Calendar className="h-3 w-3" />
            Resets {renews}
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 sm:grid-cols-2">
        <Card>
          <CardContent className="space-y-1 p-6">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4 text-rose-500" />
              Team seats
            </div>
            <div className="text-2xl font-semibold tabular-nums">
              {usage.seats_used}
              <span className="text-base font-normal text-muted-foreground">
                {usage.seats_limit === UNLIMITED ? " / unlimited" : ` / ${usage.seats_limit}`}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-1 p-6">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Database className="h-4 w-4 text-rose-500" />
              Event retention
            </div>
            <div className="text-2xl font-semibold tabular-nums">
              {usage.retention_days === UNLIMITED ? "Forever" : `${usage.retention_days} days`}
            </div>
          </CardContent>
        </Card>
      </div>

      <InvoicesCard />
    </div>
  )
}

function Meter({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0
  const near = pct >= 80
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn("h-full rounded-full transition-all", near ? "bg-rose-600" : "bg-rose-500")}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function CancelPlanButton({ onCancelled }: { onCancelled: () => void }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cancel = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch("/api/v1/saas/billing/cancel", {
        method: "POST",
        credentials: "include",
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        setError(body.error || `Couldn't cancel (${res.status})`)
        setBusy(false)
        return
      }
      onCancelled()
    } catch {
      setError("Network error. Try again.")
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}>
        <XCircle className="h-4 w-4" />
        Cancel plan
      </Button>
    )
  }
  return (
    <div className="absolute right-6 top-20 z-10 w-80 rounded-xl border border-border bg-card p-4 shadow-2xl">
      <h3 className="text-sm font-semibold">Cancel your subscription?</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        You'll keep access until the end of the current billing period. After that the workspace
        downgrades to Free. You can resubscribe any time.
      </p>
      {error && (
        <p className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-400">
          {error}
        </p>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy}>
          Keep plan
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={cancel}
          disabled={busy}
          className="bg-rose-600 text-white hover:bg-rose-500"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Confirm cancel
        </Button>
      </div>
    </div>
  )
}

function ReactivatePlanButton({ onReactivated }: { onReactivated: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reactivate = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch("/api/v1/saas/billing/reactivate", {
        method: "POST",
        credentials: "include",
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        setError(body.error || `Couldn't reactivate (${res.status})`)
        setBusy(false)
        return
      }
      onReactivated()
    } catch {
      setError("Network error. Try again.")
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button variant="outline" onClick={reactivate} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
        Reactivate plan
      </Button>
      {error && <p className="text-xs text-rose-400">{error}</p>}
    </div>
  )
}

function InvoicesCard() {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch("/api/v1/saas/billing/invoices", { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as { error?: string }
          throw new Error(body.error || `${r.status}`)
        }
        return r.json() as Promise<{ invoices: Invoice[] }>
      })
      .then((data) => {
        if (!cancelled) setInvoices(data.invoices)
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 text-sm font-medium">
          <FileText className="h-4 w-4 text-rose-500" />
          Invoices
        </div>
        {loading ? (
          <p className="mt-3 text-xs text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="mt-3 text-xs text-rose-400">{error}</p>
        ) : !invoices || invoices.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground/70">
            No invoices yet. Receipts will appear here after your first payment.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-border/60">
            {invoices.map((inv) => {
              const isCredit = inv.amount < 0
              const kindLabel = isCredit ? "Refund" : "Payment"
              const statusLabel = isCredit ? "processed" : inv.status
              return (
                <li key={inv.id} className="flex items-center justify-between py-2.5 text-sm">
                  <div>
                    <div
                      className={cn(
                        "font-medium tabular-nums",
                        isCredit && "text-emerald-500",
                      )}
                    >
                      {formatAmount(inv.amount, inv.currency)}
                    </div>
                    <div className="text-xs text-muted-foreground/70">
                      {new Date(inv.created_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                      {inv.plan ? ` · ${inv.plan}` : ""} · {kindLabel} · {statusLabel}
                    </div>
                  </div>
                  {inv.invoice_url ? (
                    <a
                      href={inv.invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300"
                    >
                      View
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function formatAmount(cents: number, currency: string): string {
  const value = cents / 100
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
      minimumFractionDigits: 2,
    }).format(value)
  } catch {
    return `${value.toFixed(2)} ${currency.toUpperCase()}`
  }
}
